"use client";
import React, { useEffect } from "react";
import UniverBackground from "../components/Background";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";

const keys = [
  { key: "⚙", label: "Setup" },
  { key: "↵", label: "New line" },
  { key: "🎤", label: "Talk", mic: true },
  { key: "⌫", label: "Delete" },
  { key: "⌨", label: "Type" },
];

// An illustration of the speech Voco is built for, not a recorded transcript.
const sample = [
  ["Chiều nay mình có ", "vi"],
  ["meeting", "en"],
  [" với ", "vi"],
  ["team", "en"],
  [", nhớ gửi ", "vi"],
  ["file slide", "en"],
  [" trước ", "vi"],
  ["deadline", "en"],
  [" nhé.", "vi"],
];

const noteSample = [
  ["Mai nhớ ", "vi"],
  ["book", "en"],
  [" phòng họp, gọi anh Minh về cái ", "vi"],
  ["contract", "en"],
  [".", "vi"],
];

const uses = [
  {
    label: "01 · Voco Keyboard",
    title: "Type with your voice, in any app",
    text: "Switch to Voco in any text field and talk. Each pause ends an utterance and Whisper types it in. Messages, email, search, forms: anywhere a keyboard goes.",
    points: [
      "Tap the mic to talk, or hold it to talk only while your finger is down.",
      "Number, phone and date fields turn spoken numbers into digits.",
      "⌨ opens a QWERTY with Telex or VNI when you'd rather type, styled after Gboard or Samsung's keyboard.",
    ],
  },
  {
    label: "02 · Voco Note",
    title: "Hold, talk, and forget it",
    text: "Phone locked? Hold the assistant key, say what's on your mind, and put the phone away. No unlocking, no app to find, nothing to tap. The note saves itself.",
    points: [
      "Set Voco as the phone's digital assistant once. On Samsung, that's the side button (Side button → Press and hold → Digital assistant).",
      "Saved after about 5 seconds of silence, or when the screen turns off.",
      "Also one tap away from the Quick Settings tile, the “Tap to record” notification, or the Voco Note icon.",
      "Optionally sends each note to your own Telegram chat, and waits while you're offline.",
    ],
  },
];

const features = [
  {
    title: "Two languages in one sentence",
    text: "English terms inside a Vietnamese sentence are written in English, not sounded out as Vietnamese syllables. You don't switch modes or pick a language first; you just talk.",
  },
  {
    title: "Only the languages you speak",
    text: "Whisper chooses among the languages you actually use, read from your phone and your other keyboards, so a short clip can't come back as one you never speak.",
  },
  {
    title: "Runs on the phone",
    text: "Whisper turbo runs through whisper.cpp, and Silero VAD separates speech from a door, music or a fan. With this default engine, audio leaves the phone only when you send a debug report yourself.",
  },
  {
    title: "Numbers and dates by voice",
    text: "“không chín một hai” gives 0912, “một trăm hai mươi lăm” gives 125, and “ngày 17 tháng 10 năm 2026” gives 17/10/2026.",
  },
];

const Mixed = ({ parts }) => (
  <>
    {parts.map(([text, lang], i) => (
      <span key={i} className={lang === "en" ? "text-[#5B8BFF]" : "text-[#FF7A6B]"}>
        {text}
      </span>
    ))}
  </>
);

const KeyboardVisual = () => (
  <div className="flex flex-col items-center gap-3 py-6">
    <div className="flex flex-row items-center gap-2 md:gap-4">
      {keys.map((k) => (
        <div key={k.label} className="flex flex-col items-center gap-2">
          <div
            className={
              k.mic
                ? "flex items-center justify-center w-[64px] h-[64px] md:w-[76px] md:h-[76px] rounded-full bg-[#1A5CFF] text-2xl md:text-3xl shadow-[0_0_0_8px_rgba(26,92,255,0.25)]"
                : "flex items-center justify-center w-[44px] h-[44px] md:w-[50px] md:h-[50px] rounded-full bg-black/30 border border-white/15 text-lg text-white"
            }
          >
            {k.key}
          </div>
          <span className="text-normal text-[10px] md:text-xs">{k.label}</span>
        </div>
      ))}
    </div>
    <span className="text-normal text-xs text-center">
      The whole keyboard. Only the keys show, over the app.
    </span>
  </div>
);

const noteSteps = ["Hold the assistant key", "Talk", "Forget it"];

const NoteVisual = () => (
  <div className="flex flex-col gap-3 w-full max-w-[360px] mx-auto py-6">
    <div className="flex flex-row items-start justify-between gap-2 pb-2">
      {noteSteps.map((step, i) => (
        <div key={step} className="flex flex-col items-center gap-2 flex-1 text-center">
          <span className="flex items-center justify-center w-8 h-8 rounded-full border border-[#FF5A47]/70 text-heading text-sm">
            {i + 1}
          </span>
          <span className="text-normal text-xs">{step}</span>
        </div>
      ))}
    </div>
    <div className="flex flex-row items-center justify-between rounded-2xl bg-black/30 border border-white/15 px-4 py-3">
      <span className="flex items-center gap-2 text-heading text-sm">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5A47] animate-pulse" />
        Voco Note
      </span>
      <span className="text-normal text-xs">Listening…</span>
    </div>
    <div className="rounded-2xl bg-black/30 border border-white/15 px-4 py-4 text-heading text-base leading-relaxed">
      <Mixed parts={noteSample} />
    </div>
    <span className="text-normal text-xs text-center">
      Saved on its own, on the phone. Copy, share or delete it later.
    </span>
  </div>
);

const visuals = [<KeyboardVisual />, <NoteVisual />];

const Voco = () => {
  useEffect(() => {
    document.title = "Voco · Endurio";
  }, []);

  return (
    <div className="relative flex w-full h-full bg-black">
      <UniverBackground url="" />
      <SiteHeader />
      <div className="absolute flex flex-col justify-center inset-x-0 items-center py-0 px-4 w-full top-[64px] md:top-[80px]">
        <div className="flex flex-col items-center text-center gap-6 max-w-[720px] pt-[80px] md:pt-[120px] pb-[60px] md:pb-[80px]">
          <img src="/voco/mark.svg" alt="Voco" className="w-[120px] h-[120px] md:w-[160px] md:h-[160px]" />
          <span className="text-heading text-[48px] md:text-[72px] leading-none">
            Voco
          </span>
          <span className="text-heading text-xl md:text-2xl">
            Voice typing for mixed languages.
          </span>
          <span className="text-normal text-base md:text-lg leading-[30px]">
            Plenty of voice keyboards handle many languages, one at a time.
            Voco is for the way people actually talk: a Vietnamese sentence
            with English words in it, typed the way you'd write it.
          </span>
          <span className="text-normal text-xs uppercase tracking-widest px-4 py-2 rounded-full border border-[#FF5A47]/60">
            Android · in private testing
          </span>
        </div>

        <div className="flex flex-col items-center gap-4 w-full max-w-[720px] pb-[80px] md:pb-[120px]">
          <div className="background-card w-full rounded-3xl border border-white/10 px-6 py-8 md:px-10 md:py-10 text-center">
            <span className="text-heading text-xl md:text-[28px] leading-relaxed">
              <Mixed parts={sample} />
            </span>
          </div>
          <div className="flex flex-row items-center gap-6 text-normal text-xs">
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#FF5A47]" />Tiếng Việt</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#1A5CFF]" />English</span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-10 md:gap-14 w-full max-w-[1200px] pb-[80px] md:pb-[120px]">
          <span className="text-heading text-[34px] md:text-[48px] text-center">
            Two ways to use it
          </span>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 w-full">
            {uses.map((u, i) => (
              <div
                key={u.label}
                className="background-card flex flex-col gap-5 p-8 md:p-10 rounded-3xl border border-white/10"
              >
                <span className="text-heading text-xs uppercase tracking-widest text-blue-1">
                  {u.label}
                </span>
                <span className="text-heading text-2xl md:text-[32px] leading-tight">
                  {u.title}
                </span>
                <span className="text-normal text-sm md:text-base leading-relaxed">
                  {u.text}
                </span>
                {visuals[i]}
                <ul className="flex flex-col gap-3">
                  {u.points.map((p) => (
                    <li key={p} className="flex flex-row gap-3 text-normal text-sm leading-relaxed">
                      <span className="text-blue-1">›</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-[1200px]">
          {features.map((f) => (
            <div
              key={f.title}
              className="background-card flex flex-col gap-4 p-8 rounded-3xl border border-white/10"
            >
              <span className="text-heading text-lg md:text-xl">{f.title}</span>
              <span className="text-normal text-sm md:text-base leading-relaxed">
                {f.text}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center text-center gap-4 max-w-[720px] pt-[80px] md:pt-[120px]">
          <span className="text-heading text-2xl md:text-[32px]">
            Want to try it?
          </span>
          <span className="text-normal text-base leading-[30px]">
            Voco isn't on the Play Store yet. Ask in our Telegram group for a
            test build.
          </span>
          <a href="https://t.me/endur_io" target="_blank" rel="noreferrer">
            <div className="border-gradient p-[2px] h-12 min-w-[160px] rounded-lg">
              <div className="h-full w-full bg-[#0E0C15] py-[14px] px-5 rounded-lg uppercase cursor-pointer text-heading text-xs flex flex-row justify-center items-center">
                Telegram
              </div>
            </div>
          </a>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default Voco;
